import { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import {
  Loader2,
  Package,
  DollarSign,
  AlertCircle,
  Upload,
  X,
  FileText,
  Paperclip,
  ArrowLeft,
  Plus,
} from 'lucide-react';
import {
  LANDED_COST_FIELD_KEYS,
  type LandedCostRow,
  newLandedCostRow,
  getAllocationPct,
  getEffectiveAmount,
  getVolumeAllocationIssues,
  LandedCostsTable,
} from './_landed-costs';

// --- Schema (same as modal) ---

const receiptItemSchema = z.object({
  poItemId: z.string(),
  purchaseOrderId: z.string(),
  orderNumber: z.string(),
  productId: z.string(),
  productName: z.string(),
  productCode: z.string(),
  orderedQty: z.number(),
  receivedQty: z.number(),
  pendingQty: z.number(),
  unitPrice: z.number(),
  receiveQty: z.number().min(0),
  unitCost: z.number().min(0),
});

const formSchema = z.object({
  receiptDate: z.string().optional(),
  warehouseId: z.string().optional(),
  supplierInvoice: z.string().optional(),
  notes: z.string().optional(),
  items: z.array(receiptItemSchema),
});

type FormData = z.infer<typeof formSchema>;

export function NewGoodsReceiptPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPoId = searchParams.get('poId');
  const utils = trpc.useUtils();

  const [selectedPoIds, setSelectedPoIds] = useState<string[]>(initialPoId ? [initialPoId] : []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [poSelectorOpen, setPoSelectorOpen] = useState(false);

  // Landed costs state — prefilled with every standard cost type so the
  // user only has to fill in the non-zero ones. Rows keep original currency,
  // KDV settings, distribution method, per-PO manual percentages and seller.
  const [landedCosts, setLandedCosts] = useState<LandedCostRow[]>(
    LANDED_COST_FIELD_KEYS.map((key) => newLandedCostRow(key))
  );

  // Document uploads
  const [uploadedDocuments, setUploadedDocuments] = useState<
    Array<{
      fileName: string;
      fileKey: string;
      fileSize: number;
      mimeType: string;
      costType: string | null;
    }>
  >([]);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Fetch approved POs
  const { data: purchaseOrders } = trpc.tradingPurchaseOrder.list.useQuery({
    status: 'APPROVED',
  });
  const { data: partiallyReceivedPOs } = trpc.tradingPurchaseOrder.list.useQuery({
    status: 'PARTIALLY_RECEIVED',
  });

  const allPOs = useMemo(() => {
    const approved = purchaseOrders?.orders ?? [];
    const partial = partiallyReceivedPOs?.orders ?? [];
    return [...approved, ...partial];
  }, [purchaseOrders, partiallyReceivedPOs]);

  // Pending items for selected POs
  const { data: pendingItems, isLoading: loadingItems } =
    trpc.tradingGoodsReceipt.getPendingItemsMulti.useQuery(selectedPoIds, {
      enabled: selectedPoIds.length > 0,
    });

  // Warehouses
  const { data: warehouses } = trpc.warehouse.list.useQuery({});

  // Sellers (account cards of type S) for landed cost association
  const { data: sellersData } = trpc.tradingCari.list.useQuery({ cariType: 'S', pageSize: 200 });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      receiptDate: new Date().toISOString().split('T')[0],
      warehouseId: '',
      supplierInvoice: '',
      notes: '',
      items: [],
    },
  });

  const { fields, replace } = useFieldArray({
    control: form.control,
    name: 'items',
  });

  // Populate items when pending items load
  useEffect(() => {
    if (pendingItems && pendingItems.length > 0) {
      replace(
        pendingItems.map((item) => ({
          poItemId: item.id,
          purchaseOrderId: item.purchaseOrderId,
          orderNumber: item.orderNumber,
          currency: (item as any).currency || 'EUR',
          productId: item.productId,
          productName: item.productName,
          productCode: item.productCode,
          orderedQty: item.orderedQty,
          receivedQty: item.receivedQty,
          pendingQty: item.pendingQty,
          unitPrice: item.unitPrice,
          receiveQty: item.pendingQty,
          unitCost: item.unitPrice,
        }))
      );
    } else if (selectedPoIds.length === 0) {
      replace([]);
    }
  }, [pendingItems, replace, selectedPoIds.length]);

  // Set default warehouse
  useEffect(() => {
    if (selectedPoIds.length > 0) {
      const firstPO = allPOs.find((po) => po.id === selectedPoIds[0]);
      if (firstPO?.warehouseId) {
        form.setValue('warehouseId', firstPO.warehouseId);
      }
    }
  }, [selectedPoIds, allPOs, form]);

  const togglePo = (poId: string) => {
    setSelectedPoIds((prev) =>
      prev.includes(poId) ? prev.filter((id) => id !== poId) : [...prev, poId]
    );
  };

  const removePo = (poId: string) => {
    setSelectedPoIds((prev) => prev.filter((id) => id !== poId));
  };

  // --- Calculations ---

  const watchedItems = form.watch('items');

  const hasItemsToReceive = useMemo(() => {
    return watchedItems.some((item) => item.receiveQty > 0);
  }, [watchedItems]);

  // Group items by PO
  const itemsByPo = useMemo(() => {
    const groups: Record<
      string,
      {
        orderNumber: string;
        supplierName: string;
        invoiceNumber: string | null;
        currency: string;
        indices: number[];
      }
    > = {};
    watchedItems.forEach((item, index) => {
      if (!groups[item.purchaseOrderId]) {
        const po = allPOs.find((p) => p.id === item.purchaseOrderId);
        groups[item.purchaseOrderId] = {
          orderNumber: item.orderNumber,
          supplierName: po?.supplierName || '',
          invoiceNumber: (po as any)?.invoiceNumber || null,
          currency: (item as Record<string, any>).currency || po?.currency || 'EUR',
          indices: [],
        };
      }
      groups[item.purchaseOrderId]!.indices.push(index);
    });
    return groups;
  }, [watchedItems, allPOs]);

  const poIdsInItems = Object.keys(itemsByPo);
  const poCount = poIdsInItems.length;

  // Per-PO subtotals (value-based)
  const poSubtotals = useMemo(() => {
    const subtotals: Record<string, number> = {};
    for (const [poId, group] of Object.entries(itemsByPo)) {
      subtotals[poId] = group.indices.reduce((sum, idx) => {
        const item = watchedItems[idx];
        return sum + (item ? item.receiveQty * item.unitCost : 0);
      }, 0);
    }
    return subtotals;
  }, [itemsByPo, watchedItems]);

  // Per-PO quantities (quantity-based)
  const poQuantities = useMemo(() => {
    const quantities: Record<string, number> = {};
    for (const [poId, group] of Object.entries(itemsByPo)) {
      quantities[poId] = group.indices.reduce((sum, idx) => {
        const item = watchedItems[idx];
        return sum + (item ? item.receiveQty : 0);
      }, 0);
    }
    return quantities;
  }, [itemsByPo, watchedItems]);

  // Landed cost totals by currency (base amounts, KDV excluded — KDV is recoverable)
  const landedCostsByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const c of landedCosts) {
      const amt = getEffectiveAmount(c);
      if (amt > 0) {
        totals[c.currency] = (totals[c.currency] ?? 0) + amt;
      }
    }
    return totals;
  }, [landedCosts]);

  // Items subtotal by currency (from PO currencies)
  const itemsSubtotalByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const [, group] of Object.entries(itemsByPo)) {
      const poTotal = group.indices.reduce((sum, idx) => {
        const item = watchedItems[idx];
        return sum + (item ? item.receiveQty * item.unitCost : 0);
      }, 0);
      if (poTotal > 0) {
        totals[group.currency] = (totals[group.currency] ?? 0) + poTotal;
      }
    }
    return totals;
  }, [itemsByPo, watchedItems]);

  // Combined grand totals by currency
  const grandTotalByCurrency = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const [cur, amt] of Object.entries(itemsSubtotalByCurrency)) {
      totals[cur] = (totals[cur] ?? 0) + amt;
    }
    for (const [cur, amt] of Object.entries(landedCostsByCurrency)) {
      totals[cur] = (totals[cur] ?? 0) + amt;
    }
    return totals;
  }, [itemsSubtotalByCurrency, landedCostsByCurrency]);

  const formatMultiCurrency = (totals: Record<string, number>) => {
    return Object.entries(totals)
      .filter(([, amt]) => amt > 0)
      .map(([cur, amt]) => formatCurrency(amt, cur))
      .join(' + ');
  };

  // Document upload handlers
  const createMutation = trpc.tradingGoodsReceipt.create.useMutation();
  const getPresignedUrl = trpc.upload.getPresignedUploadUrl.useMutation();

  const handleDocumentUpload = async (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      showErrorToast(t('purchasing.goodsReceipts.fileTooLarge'));
      return;
    }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      showErrorToast(t('purchasing.goodsReceipts.invalidFileType'));
      return;
    }
    setUploadingFile(true);
    try {
      const { uploadUrl, key } = await getPresignedUrl.mutateAsync({
        filename: file.name,
        folder: 'goods-receipt-documents',
      });
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!response.ok) throw new Error('Upload failed');
      setUploadedDocuments((prev) => [
        ...prev,
        {
          fileName: file.name,
          fileKey: key,
          fileSize: file.size,
          mimeType: file.type,
          costType: null,
        },
      ]);
    } catch (err: any) {
      showErrorToast(err.message || 'Upload failed');
    } finally {
      setUploadingFile(false);
    }
  };

  const removeDocument = (index: number) => {
    setUploadedDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDocumentCostType = (index: number, costType: string | null) => {
    setUploadedDocuments((prev) =>
      prev.map((doc, i) => (i === index ? { ...doc, costType } : doc))
    );
  };

  // --- Submit ---

  const onSubmit = async (data: FormData) => {
    const itemsToReceive = data.items.filter((item) => item.receiveQty > 0);
    if (itemsToReceive.length === 0) {
      showErrorToast(t('purchasing.goodsReceipts.noItemsError'));
      return;
    }

    // Group items by PO
    const itemsByPoId: Record<string, typeof itemsToReceive> = {};
    for (const item of itemsToReceive) {
      if (!itemsByPoId[item.purchaseOrderId]) {
        itemsByPoId[item.purchaseOrderId] = [];
      }
      itemsByPoId[item.purchaseOrderId]!.push(item);
    }

    const poIds = Object.keys(itemsByPoId);

    // Block submit when any 'volume' (manual %) cost row doesn't allocate to
    // 100% across the POs — otherwise the entered cost silently under/over-
    // inventories and distorts every derived price.
    const volumeIssues = getVolumeAllocationIssues(landedCosts, poIds);
    if (volumeIssues.length > 0) {
      showErrorToast(
        t('purchasing.goodsReceipts.volumeAllocationError', {
          rows: volumeIssues
            .map((i) => `${t(`purchasing.goodsReceipts.lc_${i.key}`)} (${i.sum.toFixed(0)}%)`)
            .join(', '),
        })
      );
      return;
    }

    // Shared batch id so sibling GRNs can be grouped on the detail view,
    // letting users verify the landed-cost split against the original totals.
    const batchId = poIds.length > 1 ? crypto.randomUUID() : undefined;
    setIsSubmitting(true);

    try {
      for (const poId of poIds) {
        const items = itemsByPoId[poId]!;

        // Build the full per-row detail payload — one entry per active cost
        // row, already sliced to this PO. Preserves original currency, KDV,
        // distribution method, seller and the allocation percentage so the
        // backend can freeze fxRates and attribute the GL posting correctly.
        const landedCostDetails = landedCosts
          .filter((c) => {
            if (c.isKdvOnly) return c.amountWithKdv > 0;
            return c.amount > 0;
          })
          .map((c) => {
            const pct = getAllocationPct(c, poId, poSubtotals, poQuantities);
            const originalAmount = c.isKdvOnly
              ? 0
              : Math.round(((c.amount * pct) / 100) * 100) / 100;
            const fullKdvOriginal = c.isKdvOnly
              ? c.amountWithKdv
              : c.kdvRate > 0
                ? Math.round(((c.amount * c.kdvRate) / 100) * 100) / 100
                : 0;
            const kdvAmountOriginal = Math.round(((fullKdvOriginal * pct) / 100) * 100) / 100;
            return {
              key: c.key,
              originalCurrency: c.currency,
              originalAmount,
              kdvRate: c.kdvRate,
              kdvAmountOriginal,
              isKdvOnly: c.isKdvOnly,
              method: c.method,
              allocationPct: pct,
              sellerId: c.sellerId,
            };
          })
          // Drop rows that fully allocated to zero for this PO (e.g. 0% share)
          .filter((d) => d.originalAmount > 0 || d.kdvAmountOriginal > 0);

        const isFirstPo = poIds.indexOf(poId) === 0;

        await createMutation.mutateAsync({
          purchaseOrderId: poId,
          receiptBatchId: batchId,
          receiptDate: data.receiptDate || undefined,
          warehouseId: data.warehouseId || undefined,
          supplierInvoice: data.supplierInvoice || undefined,
          notes: data.notes || undefined,
          landedCostDetails,
          items: items.map((item) => ({
            poItemId: item.poItemId,
            productId: item.productId,
            quantity: item.receiveQty,
            unitCost: item.unitCost,
          })),
          documents: isFirstPo && uploadedDocuments.length > 0 ? uploadedDocuments : undefined,
        });
      }

      showSuccessToast(
        poIds.length > 1
          ? t('purchasing.goodsReceipts.multiCreatedSuccess', { count: poIds.length })
          : t('purchasing.goodsReceipts.createdSuccess')
      );
      utils.tradingGoodsReceipt.list.invalidate();
      utils.tradingPurchaseOrder.list.invalidate();
      utils.stockLevel.list.invalidate();
      navigate('/trading/purchasing/receipts');
    } catch (error: any) {
      showErrorToast(error.message || t('purchasing.goodsReceipts.createError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/trading/purchasing/receipts')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t('purchasing.goodsReceipts.createTitle')}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/trading/purchasing/receipts')}>
            {tCommon('cancel')}
          </Button>
          <Button
            onClick={form.handleSubmit(onSubmit)}
            disabled={isSubmitting || !hasItemsToReceive}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('purchasing.goodsReceipts.createButton')}
          </Button>
        </div>
      </div>

      {/* Section 1: PO Selection & Warehouse */}
      <div className="border-border bg-card space-y-5 border p-5">
        <div className="grid gap-5 lg:grid-cols-3">
          {/* PO Selection */}
          <div className="space-y-3 lg:col-span-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">
                {t('purchasing.goodsReceipts.purchaseOrder')}
              </Label>
              <Popover open={poSelectorOpen} onOpenChange={setPoSelectorOpen}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="h-7 gap-1.5 text-xs">
                    <Plus className="h-3.5 w-3.5" />
                    {t('purchasing.goodsReceipts.addPurchaseOrder')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[420px] p-0" align="end">
                  <div className="border-border bg-muted/30 border-b px-3 py-2.5">
                    <p className="text-muted-foreground text-xs font-medium uppercase tracking-wider">
                      {t('purchasing.goodsReceipts.addPurchaseOrder')}
                    </p>
                  </div>
                  <div className="max-h-72 space-y-1 overflow-y-auto p-1.5">
                    {allPOs.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-6 text-center">
                        <Package className="text-muted-foreground/40 mb-2 h-8 w-8" />
                        <p className="text-muted-foreground text-sm">
                          {t('purchasing.goodsReceipts.noPendingItems')}
                        </p>
                      </div>
                    ) : (
                      allPOs.map((po) => {
                        const isSelected = selectedPoIds.includes(po.id);
                        return (
                          <label
                            key={po.id}
                            className={`flex cursor-pointer items-start gap-3 rounded-md px-3 py-2.5 transition-colors ${
                              isSelected
                                ? 'bg-primary/5 border-primary/20 border'
                                : 'hover:bg-muted/50 border border-transparent'
                            }`}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => togglePo(po.id)}
                              className="mt-0.5"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-medium">
                                  {po.orderNumber}
                                </span>
                                <span className="text-muted-foreground truncate text-sm">
                                  {po.supplierName}
                                </span>
                              </div>
                              {po.invoiceNumber && (
                                <p className="text-muted-foreground mt-0.5 text-xs">
                                  {t('purchasing.purchaseOrders.invoiceNumber')}:{' '}
                                  <span className="font-mono">{po.invoiceNumber}</span>
                                </p>
                              )}
                            </div>
                            <span className="text-muted-foreground bg-muted/60 shrink-0 rounded px-1.5 py-0.5 text-xs tabular-nums">
                              {po.itemCount} {t('purchasing.goodsReceipts.items').toLowerCase()}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {selectedPoIds.length === 0 ? (
              <div className="border-border flex items-center justify-center rounded-md border border-dashed py-6 text-center">
                <div>
                  <Package className="text-muted-foreground/30 mx-auto mb-1.5 h-8 w-8" />
                  <p className="text-muted-foreground text-sm">
                    {t('purchasing.goodsReceipts.noPendingItems')}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedPoIds.map((poId) => {
                  const po = allPOs.find((p) => p.id === poId);
                  if (!po) return null;
                  return (
                    <div
                      key={poId}
                      className="border-border bg-muted/20 flex items-center gap-3 rounded-md border px-3 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium">{po.orderNumber}</span>
                          <span className="text-muted-foreground text-xs">{po.supplierName}</span>
                        </div>
                        {po.invoiceNumber && (
                          <p className="text-muted-foreground mt-0.5 text-xs">
                            {t('purchasing.purchaseOrders.invoiceNumber')}:{' '}
                            <span className="font-mono">{po.invoiceNumber}</span>
                          </p>
                        )}
                      </div>
                      <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
                        {po.itemCount} {t('purchasing.goodsReceipts.items').toLowerCase()}
                      </span>
                      <button
                        type="button"
                        onClick={() => removePo(poId)}
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 shrink-0 rounded-md p-1 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Receipt Date */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">
                {t('purchasing.goodsReceipts.receiptDate')}
              </Label>
              <input
                type="date"
                {...form.register('receiptDate')}
                className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-10 w-full border px-3 text-sm focus:outline-none focus:ring-1"
              />
            </div>

            {/* Warehouse */}
            <div className="space-y-3">
              <Label className="text-sm font-semibold">
                {t('purchasing.goodsReceipts.receiveToWarehouse')}
              </Label>
              <Select
                value={form.watch('warehouseId') || ''}
                onValueChange={(value) => form.setValue('warehouseId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('purchasing.goodsReceipts.usePOWarehouse')} />
                </SelectTrigger>
                <SelectContent>
                  {warehouses?.map((wh) => (
                    <SelectItem key={wh.id} value={wh.id}>
                      {wh.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Items Table */}
      {selectedPoIds.length > 0 && (
        <div className="border-border bg-card overflow-hidden border">
          <div className="border-border border-b px-4 py-3">
            <Label className="flex items-center gap-2 text-sm font-semibold">
              <Package className="h-4 w-4" />
              {t('purchasing.goodsReceipts.itemsToReceive')}
            </Label>
          </div>

          {loadingItems ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : fields.length === 0 ? (
            <div className="p-6 text-center">
              <AlertCircle className="text-muted-foreground mx-auto h-8 w-8" />
              <p className="text-muted-foreground mt-2 text-sm">
                {t('purchasing.goodsReceipts.noPendingItems')}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  {poCount > 1 && (
                    <th className="px-3 py-2 text-left">
                      {t('purchasing.goodsReceipts.purchaseOrder')}
                    </th>
                  )}
                  <th className="px-3 py-2 text-left">{t('purchasing.goodsReceipts.product')}</th>
                  <th className="px-3 py-2 text-right">{t('purchasing.goodsReceipts.ordered')}</th>
                  <th className="px-3 py-2 text-right">{t('purchasing.goodsReceipts.received')}</th>
                  <th className="px-3 py-2 text-right">{t('purchasing.goodsReceipts.pending')}</th>
                  <th className="w-24 px-3 py-2 text-right">
                    {t('purchasing.goodsReceipts.receiveQty')}
                  </th>
                  <th className="w-28 px-3 py-2 text-right">
                    {t('purchasing.goodsReceipts.unitCost')}
                  </th>
                  <th className="px-3 py-2 text-right">
                    {t('purchasing.goodsReceipts.lineTotal')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {Object.entries(itemsByPo).map(([_poId, group]) =>
                  group.indices.map((index, i) => {
                    const field = fields[index]!;
                    const receiveQty = form.watch(`items.${index}.receiveQty`);
                    const unitCost = form.watch(`items.${index}.unitCost`);
                    const lineTotal = receiveQty * unitCost;

                    return (
                      <tr key={field.id} className="hover:bg-muted/30">
                        {poCount > 1 && (
                          <td className="px-3 py-2">
                            {i === 0 && (
                              <div>
                                <p className="font-mono text-xs">{group.orderNumber}</p>
                                <p className="text-muted-foreground text-xs">
                                  {group.supplierName}
                                </p>
                              </div>
                            )}
                          </td>
                        )}
                        <td className="px-3 py-2">
                          <div>
                            <p className="font-medium">{field.productName}</p>
                            <p className="text-muted-foreground font-mono text-xs">
                              {field.productCode}
                            </p>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">{field.orderedQty}</td>
                        <td className="text-muted-foreground px-3 py-2 text-right">
                          {field.receivedQty}
                        </td>
                        <td className="px-3 py-2 text-right font-medium">{field.pendingQty}</td>
                        <td className="px-3 py-2">
                          <Controller
                            control={form.control}
                            name={`items.${index}.receiveQty`}
                            render={({ field: f }) => (
                              <NumberInput
                                value={f.value}
                                onValueChange={(v) => f.onChange(v ?? 0)}
                                decimalScale={0}
                                isAllowed={({ floatValue }) =>
                                  floatValue === undefined ||
                                  (floatValue >= 0 && floatValue <= field.pendingQty)
                                }
                                placeholder="0"
                                className="h-8 w-full text-right"
                              />
                            )}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Controller
                            control={form.control}
                            name={`items.${index}.unitCost`}
                            render={({ field: f }) => (
                              <NumberInput
                                value={f.value}
                                onValueChange={(v) => f.onChange(v ?? 0)}
                                decimalScale={2}
                                placeholder="0.00"
                                className="h-8 w-full text-right"
                              />
                            )}
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-medium">
                          {formatCurrency(lineTotal, group.currency)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot className="bg-muted/30">
                <tr>
                  <td colSpan={poCount > 1 ? 7 : 6} className="px-3 py-2 text-right font-medium">
                    {t('purchasing.goodsReceipts.itemsSubtotal')}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {formatMultiCurrency(itemsSubtotalByCurrency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* Section 3: Landed Costs Table */}
      {selectedPoIds.length > 0 && fields.length > 0 && (
        <LandedCostsTable
          landedCosts={landedCosts}
          setLandedCosts={setLandedCosts}
          pos={poIdsInItems.map((poId) => ({
            id: poId,
            orderNumber: itemsByPo[poId]?.orderNumber ?? '',
            supplierName: itemsByPo[poId]?.supplierName ?? '',
            invoiceNumber: itemsByPo[poId]?.invoiceNumber ?? null,
            currency: itemsByPo[poId]?.currency ?? 'EUR',
          }))}
          poSubtotals={poSubtotals}
          poQuantities={poQuantities}
          sellers={sellersData?.items ?? []}
        />
      )}

      {/* Section 4: Documents */}
      {selectedPoIds.length > 0 && fields.length > 0 && (
        <div className="border-border bg-card space-y-3 border p-4">
          <Label className="flex items-center gap-2 text-sm font-semibold">
            <Paperclip className="h-4 w-4" />
            {t('purchasing.goodsReceipts.documents')}
          </Label>
          <p className="text-muted-foreground text-xs">
            {t('purchasing.goodsReceipts.documentsDescription')}
          </p>

          {uploadedDocuments.length > 0 && (
            <div className="space-y-2">
              {uploadedDocuments.map((doc, index) => (
                <div key={index} className="bg-background flex items-center gap-2 border px-3 py-2">
                  <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate text-sm">{doc.fileName}</span>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {(doc.fileSize / 1024).toFixed(0)} KB
                  </span>
                  <Select
                    value={doc.costType || '__general__'}
                    onValueChange={(value) =>
                      updateDocumentCostType(index, value === '__general__' ? null : value)
                    }
                  >
                    <SelectTrigger className="h-7 w-[180px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__general__">
                        {t('purchasing.goodsReceipts.documentCostTypeGeneral')}
                      </SelectItem>
                      {LANDED_COST_FIELD_KEYS.map((key) => (
                        <SelectItem key={key} value={key}>
                          {t(`purchasing.goodsReceipts.lc_${key}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => removeDocument(index)}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div
            className="border-muted-foreground/25 hover:border-primary/50 flex cursor-pointer items-center justify-center border-2 border-dashed px-4 py-3 transition-colors"
            onClick={() => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.pdf,.jpg,.jpeg,.png';
              input.multiple = true;
              input.onchange = (e) => {
                const files = (e.target as HTMLInputElement).files;
                if (files) Array.from(files).forEach((file) => handleDocumentUpload(file));
              };
              input.click();
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const files = e.dataTransfer.files;
              if (files) Array.from(files).forEach((file) => handleDocumentUpload(file));
            }}
          >
            {uploadingFile ? (
              <div className="flex items-center gap-2">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                <span className="text-muted-foreground text-xs">
                  {t('purchasing.goodsReceipts.uploading')}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Upload className="text-muted-foreground h-4 w-4" />
                <span className="text-muted-foreground text-xs">
                  {t('purchasing.goodsReceipts.addDocument')}
                </span>
                <span className="text-muted-foreground/70 text-[10px]">
                  ({t('purchasing.goodsReceipts.acceptedFormats')})
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section 5: Notes */}
      <div className="border-border bg-card space-y-2 border p-4">
        <Label>{t('purchasing.goodsReceipts.notes')}</Label>
        <Textarea
          {...form.register('notes')}
          placeholder={t('purchasing.goodsReceipts.notesPlaceholder')}
          rows={2}
        />
      </div>

      {/* Section 6: Summary */}
      {selectedPoIds.length > 0 && hasItemsToReceive && (
        <div className="bg-primary/5 border-primary/20 border p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <DollarSign className="text-primary h-5 w-5" />
                <span className="font-medium">{t('purchasing.goodsReceipts.grandTotal')}</span>
              </div>
              {Object.keys(landedCostsByCurrency).length > 0 && (
                <p className="text-muted-foreground mt-1 text-xs">
                  {t('purchasing.goodsReceipts.itemsLabel')}:{' '}
                  {formatMultiCurrency(itemsSubtotalByCurrency)} +{' '}
                  {t('purchasing.goodsReceipts.landedCostsTotal')}:{' '}
                  {formatMultiCurrency(landedCostsByCurrency)}
                </p>
              )}
              {poCount > 1 && (
                <p className="text-muted-foreground mt-1 text-xs">
                  {t('purchasing.goodsReceipts.multiReceiptNote', { count: poCount })}
                </p>
              )}
            </div>
            <span className="text-xl font-bold">{formatMultiCurrency(grandTotalByCurrency)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
